import clsx from "clsx";
import gsap from "gsap";
import {
  type CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSwipeable } from "react-swipeable";

import type { SliderCardBase } from "@shared/content-types/schemas/sections.schema";
import { UISwiperNavButtons } from "@shared/ui/slider-nav-buttons/slider-nav-buttons";
import { round } from "@shared/utils";

import { SliderCard } from "./ui";

import s from "./cards-gallery.module.scss";

interface CardsGalleryProps {
  items: SliderCardBase[];
  isMobile?: boolean;
}

export const CardsGallery = (props: CardsGalleryProps) => {
  const { items } = props;

  if (!items?.length) return null;

  if (items.length === 1 && items[0]) {
    return <CardsGallerySingle item={items[0]} />;
  }

  return (
    <CardsGalleryMulti
      key={
        items
          ?.map((i) => i?.id)
          .filter(Boolean)
          .join("_") ?? "gallery"
      }
      {...props}
    />
  );
};

export const CardsGallerySingle = ({ item }: { item: SliderCardBase }) => {
  return (
    <div className={clsx(s.slider, s.sliderPrimary, s.sliderSingle)}>
      <SliderCard
        {...item}
        active
        variant="primary"
        className={s.slidePrimary}
      />
    </div>
  );
};

const getInfinityPosition = (
  elPosition: number,
  whole: number,
  offset: number,
): number => {
  if (whole === offset) {
    return elPosition;
  }

  return (((elPosition % whole) + whole + offset) % whole) - offset;
};

export const CardsGalleryMulti = ({ items, isMobile }: CardsGalleryProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const paginationRef = useRef<HTMLDivElement | null>(null);

  const handleNextSlideChange = () => {
    setActiveIndex((prevState) => {
      return prevState + 1;
    });
  };

  const handlePrevSlideChange = () => {
    setActiveIndex((prevState) => {
      return prevState - 1;
    });
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      handleNextSlideChange();
    },
    onSwipedRight: () => {
      handlePrevSlideChange();
    },
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const $items = useRef<HTMLDivElement[]>([]);
  const $container = useRef<HTMLDivElement | null>(null);
  const $progress = useRef({
    p: 0,
  });

  useEffect(() => {
    const resize = () => {
      const heights: number[] = [];

      $container.current?.style.removeProperty("--slider-height");

      $items.current.forEach((item) => {
        item.style.removeProperty("height");
        heights.push(item?.clientHeight || 0);
      });

      const maxHeight = Math.max(...heights);

      $container.current?.style.setProperty(
        "--slider-height",
        `${maxHeight}px`,
      );

      $items.current.forEach((item) => {
        item.style.setProperty("height", `var(--slider-height)`);
      });

      window?.__GLOBAL_SCROLL__?.resize();
    };

    resize();

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  useEffect(() => {
    gsap.to($progress.current, {
      p: activeIndex,
      overwrite: "auto",
      duration: 0.4,
      ease: "custom-effect",
      onUpdate: () => {
        $items.current.forEach((item, idx) => {
          const position = getInfinityPosition(
            idx - $progress.current.p,
            items.length,
            1,
          );
          item.style.setProperty("--progress", `${round(position, 5)}`);
          item.style.zIndex = `${Math.round(position)}`;
        });
      },
    });
  }, [activeIndex]);

  const realIndex = useMemo(() => {
    return getInfinityPosition(activeIndex, items.length, 0);
  }, [activeIndex]);

  return (
    <div
      className={s.root}
      ref={$container}
      style={
        {
          "--items-count": items?.length,
        } as CSSProperties
      }
    >
      <div className={s.sliderContainer} {...handlers}>
        {items.map((item, idx) => {
          return (
            <div
              key={`slide-${item?.id}`}
              className={s.item}
              ref={(ref) => {
                if (ref) $items.current[idx] = ref;
              }}
            >
              <SliderCard
                variant="primary"
                active={
                  items.length <= 1 ||
                  getInfinityPosition(idx - activeIndex, items.length, 1) <= 0
                }
                className={s.slidePrimary}
                {...item}
              />
            </div>
          );
        })}

        <UISwiperNavButtons
          variant="dark"
          totalSlides={items.length}
          slideNext={handleNextSlideChange}
          slidePrev={handlePrevSlideChange}
          currentIndex={realIndex}
          className={s.navigation}
        />
      </div>
      <div ref={paginationRef} className={clsx(s.pagination)}>
        {items.map((item, idx) => (
          <div
            className={clsx(s.bullet, {
              [s.bulletActive]: realIndex === idx,
            })}
            key={`slide-${item?.id}`}
          ></div>
        ))}
      </div>
    </div>
  );
};
